/**
 * @fileoverview
 * Branch Service - Manages branch master data
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, of, map } from 'rxjs';
import { ApiService } from '@core/http';
import { API_ENDPOINTS } from '@core/constants';

export interface Branch {
  id: number;
  uuid: string;
  name: string;
  code: string;
  regionId: number;
  regionUuid: string;
  isActive: boolean;
  /** Backend field names from branch master */
  latitude?: number | string;
  longitude?: number | string;
  /** Normalized aliases used by some clients */
  geoLat?: number;
  geoLng?: number;
}

export interface BranchDropdownOption {
  value: string; // uuid
  label: string; // name
  id: number;
  regionId?: number;
  regionUuid?: string;
  geoLat?: number;
  geoLng?: number;
}

@Injectable({
  providedIn: 'root'
})
export class BranchService {
  private apiService = inject(ApiService);

  // State signals for dropdown
  private branchesSignal = signal<BranchDropdownOption[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public readonly signals
  readonly branches = this.branchesSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  /**
   * Get all branches for dropdown.
   * @param all - When true, request all branches regardless of user access (backend query param).
   */
  getBranchesForDropdown(search?: string, limit?: number, regionUuid?: string, all?: boolean): Observable<{ data: BranchDropdownOption[] }> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    // Build query parameters
    const params = new URLSearchParams();
    if (all === true) params.append('all', 'true');
    if (search) params.append('search', search);
    if (limit !== undefined) params.append('limit', String(limit));
    if (regionUuid) params.append('regionUuid', regionUuid);
    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.BRANCHES.DROPDOWN}?${queryString}` : API_ENDPOINTS.BRANCHES.DROPDOWN;

    return this.apiService.get<{ data: Branch[] }>(url).pipe(
      map(response => {
        const dropdownOptions = (response.data || []).map(branch =>
          this.toDropdownOption(branch)
        );
        this.branchesSignal.set(dropdownOptions);
        this.loadingSignal.set(false);
        return { data: dropdownOptions };
      }),
      catchError(error => {
        this.errorSignal.set(error.message || 'Failed to load branches');
        this.loadingSignal.set(false);
        this.branchesSignal.set([]);
        return of({ data: [] });
      })
    );
  }

  /**
   * Get branches by region UUID
   */
  getBranchesByRegion(regionUuid: string, search?: string, limit?: number): Observable<{ data: BranchDropdownOption[] }> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    // Build query parameters
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit !== undefined) params.append('limit', String(limit));
    const queryString = params.toString();
    const baseUrl = API_ENDPOINTS.BRANCHES.BY_REGION(regionUuid);
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    return this.apiService.get<{ data: Branch[] }>(url).pipe(
      map(response => {
        const dropdownOptions = (response.data || []).map(branch =>
          this.toDropdownOption(branch)
        );
        this.branchesSignal.set(dropdownOptions);
        this.loadingSignal.set(false);
        return { data: dropdownOptions };
      }),
      catchError(error => {
        this.errorSignal.set(error.message || 'Failed to load branches');
        this.loadingSignal.set(false);
        this.branchesSignal.set([]);
        return of({ data: [] });
      })
    );
  }

  private toDropdownOption(branch: Branch): BranchDropdownOption {
    return {
      value: branch.uuid,
      label: branch.name,
      id: branch.id,
      regionId: branch.regionId,
      regionUuid: branch.regionUuid,
      geoLat: this.toCoordinate(branch.geoLat ?? branch.latitude),
      geoLng: this.toCoordinate(branch.geoLng ?? branch.longitude),
    };
  }

  private toCoordinate(value: number | string | undefined | null): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : undefined;
  }

  /**
   * Get branch by UUID
   */
  getBranchByUuid(uuid: string): Observable<Branch> {
    return this.apiService.get<Branch>(API_ENDPOINTS.BRANCHES.BY_UUID(uuid));
  }

  /**
   * Clear cached branches (force reload)
   */
  clearCache(): void {
    this.branchesSignal.set([]);
    this.errorSignal.set(null);
  }
}
